import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export type BillingPeriod = 'monthly' | 'yearly';

/**
 * Billing period toggle component with radio-style buttons
 * Supports monthly/yearly with optional savings badge
 * 
 * Usage:
 * <billing-toggle 
 *   [value]="billingPeriod()" 
 *   [savingsText]="'Save up to 20%'"
 *   (valueChange)="onBillingChange($event)">
 * </billing-toggle>
 */
@Component({
  selector: 'billing-toggle',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './billing-toggle.component.html'
})
export class BillingToggleComponent {
  /** Current billing period value */
  readonly value = input<BillingPeriod>('monthly');
  
  /** Size variant */
  readonly size = input<'sm' | 'md'>('md');
  
  /** Custom monthly label */
  readonly monthlyLabel = input<string>('Monthly');
  
  /** Custom yearly label */
  readonly yearlyLabel = input<string>('Annually');
  
  /** Savings badge text (set empty to hide) */
  readonly savingsText = input<string>('Save up to 20%');
  
  /** Show/hide savings badge */
  readonly showSavingsBadge = input<boolean>(true);
  
  /** Emits when billing period changes */
  readonly valueChange = output<BillingPeriod>();
  
  // Computed classes based on size
  readonly containerClass = computed(() => 
    this.size() === 'sm' ? 'gap-3' : 'gap-4'
  );
  
  readonly optionClass = computed(() => 
    this.size() === 'sm' ? 'gap-3' : 'gap-4'
  );
  
  readonly radioSizeClass = computed(() => 
    this.size() === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'
  );
  
  readonly dotSizeClass = computed(() => 
    this.size() === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2'
  );
  
  selectPeriod(period: BillingPeriod) {
    if (this.value() !== period) {
      this.valueChange.emit(period);
    }
  }
}
